import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import i18n from 'i18n/i18n'
import { detectBrowserLanguage } from 'i18n/detectBrowserLanguage'
import { useAuthTenant, useAuthLoggedIn } from 'store/auth'
import { getProtectedTransparency } from 'services/transparency-service'
import { Main as Container } from 'Components/Main'
import { PageHeader } from 'Components/ui'
import { OverviewTab } from './components/OverviewTab'
import { FrameworksTab } from './components/FrameworksTab'
import { GovernanceStructure } from './components/GovernanceStructure'
import { PoliciesTab } from './components/PoliciesTab'
import {
  ExternalViewerContainer,
  ProtectedHero,
  ProtectedHeroInner,
  ProtectedHeroLabel,
  ProtectedHeroLabelText,
  ProtectedHeroTitle,
  ProtectedHeroCaption,
  ExternalContent,
  PillTabBar,
  PillTab,
  TabContent,
  TabBar,
  LoadingContainer,
  ErrorContainer,
  ErrorTitle,
  ErrorMessage,
  Spinner,
} from './styles'
import { DefaultToggleGroup } from 'Components/common/DefaultToggleGroup'
import { DefaultToggleOption } from 'Components/common/DefaultToggleGroup/DefaultToggleOption'

const ShieldSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--vo-capri)">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
  </svg>
)

export const ProtectedTransparencyView = ({
  companyKey: companyKeyProp,
  accessToken: accessTokenProp,
  isExternalViewer: isExternalViewerProp = false,
}) => {
  const { t } = useTranslation('transparency')
  const [searchParams] = useSearchParams()
  const accessToken = accessTokenProp || searchParams.get('token')
  const companyKeyParam = searchParams.get('companyKey')

  const isLoggedIn = useSelector(useAuthLoggedIn)
  const selectedTenant = useSelector(useAuthTenant)

  const companyKey = companyKeyProp || companyKeyParam || selectedTenant?.slug

  const [activeTab, setActiveTab] = useState('overview')

  const { isLoading, error, data } = useQuery(
    ['protectedTransparency', companyKey, accessToken],
    () => getProtectedTransparency(companyKey, accessToken),
    { enabled: !!companyKey }
  )

  const isExternalViewer = isExternalViewerProp || (!!accessToken && !isLoggedIn)

  useEffect(() => {
    if (isExternalViewer) {
      i18n.changeLanguage(detectBrowserLanguage())
    }
  }, [isExternalViewer])

  const responseData = data?.data

  useEffect(() => {
    document.title = t('protected.heading', { name: responseData?.organization?.name || '' })
    let metaRobots = document.querySelector('meta[name="robots"]')
    if (!metaRobots) {
      metaRobots = document.createElement('meta')
      metaRobots.name = 'robots'
      document.head.appendChild(metaRobots)
    }
    metaRobots.content = 'noindex, nofollow'
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow'
      }
    }
  }, [responseData?.organization?.name, t])

  const handleTabChange = (_, value) => {
    if (value) setActiveTab(value)
  }

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    )
  }

  if (error || !responseData) {
    const errorContent = (
      <ErrorContainer>
        <ErrorTitle>{t('protected.errorTitle')}</ErrorTitle>
        <ErrorMessage>{t('protected.errorMessage')}</ErrorMessage>
      </ErrorContainer>
    )

    if (isExternalViewer) {
      return <ExternalViewerContainer>{errorContent}</ExternalViewerContainer>
    }

    return (
      <Container>
        <PageHeader title={t('protected.title')} />
        {errorContent}
      </Container>
    )
  }

  const { organization, frameworks, governanceStructure, policies } = responseData

  const tabs = [
    { key: 'overview', label: t('shared.tabs.overview') },
    { key: 'frameworks', label: t('shared.tabs.frameworks') },
    { key: 'governance', label: t('shared.tabs.governance') },
    { key: 'policies', label: t('shared.tabs.policies') },
  ]

  const tabContent = (
    <>
      <PillTabBar>
        {tabs.map(tab => (
          <PillTab
            key={tab.key}
            $active={activeTab === tab.key}
            data-active={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </PillTab>
        ))}
      </PillTabBar>

      <TabContent>
        {activeTab === 'overview' && (
          <OverviewTab
            organization={organization}
            frameworks={frameworks}
            governanceStructure={governanceStructure}
          />
        )}
        {activeTab === 'frameworks' && (
          <FrameworksTab
            frameworks={frameworks}
            companyKey={companyKey}
            accessToken={accessToken}
          />
        )}
        {activeTab === 'governance' && (
          <GovernanceStructure
            categories={governanceStructure?.categories}
            overallPosture={governanceStructure?.overallPosture}
            showSubDomains
          />
        )}
        {activeTab === 'policies' && (
          <PoliciesTab
            policies={policies}
            companyKey={companyKey}
            accessToken={accessToken}
          />
        )}
      </TabContent>
    </>
  )

  if (isExternalViewer) {
    return (
      <ExternalViewerContainer>
        <ProtectedHero>
          <ProtectedHeroInner>
            <ProtectedHeroLabel>
              <ShieldSvg />
              <ProtectedHeroLabelText>{t('protected.accessProtected')}</ProtectedHeroLabelText>
            </ProtectedHeroLabel>
            <ProtectedHeroTitle>{organization?.name}</ProtectedHeroTitle>
            <ProtectedHeroCaption>{t('shared.transparencyCenter')}</ProtectedHeroCaption>
          </ProtectedHeroInner>
        </ProtectedHero>
        <ExternalContent>
          {tabContent}
        </ExternalContent>
      </ExternalViewerContainer>
    )
  }

  return (
    <Container>
      <PageHeader
        title={t('protected.title')}
        segments={[
          { label: t('common:breadcrumbs.reports') },
          { label: t('protected.title') },
        ]}
      />
      <TabBar>
        <DefaultToggleGroup value={activeTab} onChange={handleTabChange} exclusive>
          {tabs.map(tab => (
            <DefaultToggleOption key={tab.key} value={tab.key}>{tab.label}</DefaultToggleOption>
          ))}
        </DefaultToggleGroup>
      </TabBar>
      <TabContent>
        {activeTab === 'overview' && (
          <OverviewTab organization={organization} frameworks={frameworks} governanceStructure={governanceStructure} />
        )}
        {activeTab === 'frameworks' && (
          <FrameworksTab frameworks={frameworks} companyKey={companyKey} accessToken={accessToken} />
        )}
        {activeTab === 'governance' && (
          <GovernanceStructure categories={governanceStructure?.categories} overallPosture={governanceStructure?.overallPosture} showSubDomains />
        )}
        {activeTab === 'policies' && (
          <PoliciesTab policies={policies} companyKey={companyKey} accessToken={accessToken} />
        )}
      </TabContent>
    </Container>
  )
}
